import { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { clearAllDrafts, suspendDraftWrites } from '../state/draft-store.ts';
import { clearAllInspectionDrafts, suspendInspectionDraftWrites } from '../state/inspection-draft-store.ts';
import { Alert, ConfirmDialog } from './ui.tsx';
import { OfflineStatusBadge } from './offline-status-badge.tsx';
import { useOnlineStatus } from '../hooks/index.ts';
import NotificationBell from './notification-bell.tsx';
import { useVisitCoverage } from './staff-visit-offers-link.tsx';
import { Icon } from './icons.tsx';
import { AccountMenu, AppShell, LocationSwitcher, type ShellNavItem } from './app-shell.tsx';
import { CREW_GROUPS, CREW_NAV_ITEMS, CREW_PHONE_TAB_ORDER, reachableNavItems } from './nav-config.ts';

/**
 * The crew's frame: the office's sidebar, rail and phone bar (app-shell.tsx),
 * with a cleaner's destinations in them. Most crew use it on a phone, where
 * their jobs, the job chat and the staff hub are each one tap from the bottom
 * bar, and their account is under More.
 */
export default function CrewLayout() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const { user, company, can, currentLocation, locations, switchLocation, impersonatedBy } = session;
  const isOnline = useOnlineStatus();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [error, setError] = useState('');

  const visitCoverage = useVisitCoverage();
  const items: ShellNavItem[] = useMemo(
    () =>
      reachableNavItems(CREW_NAV_ITEMS, {
        can,
        company,
        // Neither is offered to crew: Feedback is an office tool, and the crew
        // allowlist does not include Help.
        feedbackEnabled: false,
        helpAssistant: false,
        visitCoverage,
      }).map((item) => ({ to: item.to, label: t(item.labelKey), icon: item.icon, group: item.group })),
    [can, company, visitCoverage, t],
  );
  const groups = useMemo(() => CREW_GROUPS.map((group) => ({ id: group.id, label: t(group.labelKey) })), [t]);
  const brand = useMemo(() => ({ to: '/my-jobs', label: t('appLayout.brandedName') }), [t]);
  const onMoreOpenChange = useCallback((open: boolean) => setMoreOpen(open), []);

  /** Ends the session; `returnTo` sends an impersonating operator back to the CMS. */
  async function signOut(returnTo?: string): Promise<boolean> {
    setSigningOut(true);
    setError('');
    try {
      try {
        await authApi.logout();
      } catch (e) {
        if (!(e instanceof ApiRequestError && e.status === 401)) throw e;
      }
      suspendDraftWrites();
      suspendInspectionDraftWrites();
      session.clearSession();
      await Promise.all([clearAllDrafts(), clearAllInspectionDrafts()]);
      if (returnTo) window.location.assign(returnTo);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <AppShell
      items={items}
      groups={groups}
      phoneTabOrder={CREW_PHONE_TAB_ORDER}
      brand={brand}
      moreOpen={moreOpen}
      onMoreOpenChange={onMoreOpenChange}
      mainClassName="mx-auto max-w-5xl p-4 sm:p-6"
      banner={
        impersonatedBy ? (
          <div className="bg-[color:var(--color-danger)] text-white print:hidden">
            <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
              <p className="font-medium">
                {t('appLayout.impersonationBanner', {
                  admin: impersonatedBy,
                  user: user?.fullName || user?.email || '',
                })}
              </p>
              <button
                type="button"
                className="rounded-md border border-white/70 px-3 py-1 font-semibold hover:bg-white/10 disabled:opacity-60"
                disabled={signingOut}
                onClick={() => void signOut(`/admin/companies/${user?.companyId ?? ''}`)}
              >
                {t('appLayout.impersonationEnd')}
              </button>
            </div>
          </div>
        ) : null
      }
      topBar={
        <>
          <p className="flex min-w-0 flex-1 basis-0 items-center gap-2 font-semibold text-[color:var(--color-ink)]">
            <Icon name="company" className="hidden shrink-0 text-[color:var(--color-muted)] sm:block" />
            <span className="truncate">{company?.name ?? t('appLayout.brandedName')}</span>
          </p>
          {locations.length >= 2 ? (
            <LocationSwitcher currentLocation={currentLocation} locations={locations} onSwitch={switchLocation} />
          ) : null}
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            <NotificationBell />
            <span className="hidden sm:contents">
              <OfflineStatusBadge isOnline={isOnline} />
            </span>
            {user ? (
              <AccountMenu
                user={user}
                settings={{ to: '/crew-account', label: t('crew.account') }}
                signingOut={signingOut}
                onSignOut={() => setConfirmingSignOut(true)}
              >
                <label className="flex items-center justify-between gap-2 text-sm">
                  {t('crew.language')}
                  <select
                    aria-label={t('crew.language')}
                    className="field-control w-auto"
                    value={i18n.language.startsWith('es') ? 'es' : 'en'}
                    onChange={(e) => void i18n.changeLanguage(e.target.value)}
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                  </select>
                </label>
              </AccountMenu>
            ) : null}
            {confirmingSignOut ? (
              <ConfirmDialog
                title={t('appLayout.signOutConfirmTitle')}
                confirmLabel={t('appLayout.signOut')}
                cancelLabel={t('appLayout.signOutConfirmStay')}
                busy={signingOut}
                error={error || null}
                onCancel={() => {
                  setConfirmingSignOut(false);
                  setError('');
                }}
                onConfirm={() => {
                  void signOut().then((ended) => {
                    if (ended) setConfirmingSignOut(false);
                  });
                }}
              >
                <p>{t('appLayout.signOutConfirmBody')}</p>
              </ConfirmDialog>
            ) : null}
          </div>
        </>
      }
      topBarPhoneLine={<OfflineStatusBadge isOnline={isOnline} />}
    >
      {error && !confirmingSignOut ? <Alert tone="error">{error}</Alert> : null}
      {company?.accessLocked && location.pathname !== '/crew-account' ? (
        <Alert>{t('crew.locked')}</Alert>
      ) : (
        // Remounted when the person or the location changes: a location
        // switch can turn a manager into crew, and nothing of the page they
        // were on may survive under the new role.
        <div key={`${user?.id}:${currentLocation?.id}`}>
          <Outlet />
        </div>
      )}
    </AppShell>
  );
}
